import React from 'react';
import { formatDate, getStaffSizeLabel, getAdminStatusLabel, getESSLabel } from '../../utils/inseeCodeMappings';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { CompanyData } from '../../types/inpi';
import DirectorsCard from '../inpi/DirectorsCard';

interface NAFInfo {
  code: string;
  intitule: string;
  intitule65?: string;
  intitule40?: string;
  niveau: string;
}

interface CompanyCategory {
  code: string;
  level: number;
  label: string;
}

interface UniteLegale {
  denominationUniteLegale: string;
  activitePrincipaleUniteLegale: string;
  categorieJuridiqueUniteLegale: string;
  trancheEffectifsUniteLegale: string;
  dateCreationUniteLegale: string;
  etatAdministratifUniteLegale: string;
  sigleUniteLegale: string;
  economieSocialeSolidaireUniteLegale: string;
  societeMissionUniteLegale: string;
  caractereEmployeurUniteLegale: string;
  etablissementCount: number;
  nafInfo: NAFInfo | null;
  categorieJuridique: CompanyCategory | null;
  nomUniteLegale?: string;
  prenom1UniteLegale?: string;
  etablissementSiege?: {
    libelleCommuneEtablissement: string;
  };
}

interface CompanyOverviewSectionProps {
  siren: string;
  uniteLegale: UniteLegale | null;
  companyData: CompanyData | null;
  companyDataLoading: boolean;
  companyError: string | null;
}

const CompanyOverviewSection: React.FC<CompanyOverviewSectionProps> = ({ 
  siren,
  uniteLegale, 
  companyData,
  companyDataLoading,
  companyError
}) => {
  // Helper to check if data is for entrepreneur individuel
  const isEntrepreneurIndividuel = () => {
    return companyData?.formality?.typePersonne === 'P' || 
           companyData?.formality?.content?.personnePhysique !== undefined;
  };

  // Helper to get capital information from INPI data
  const getCapitalInfo = () => {
    if (!companyData) return 'Non renseigné';
    
    // Only applicable for companies, not for entrepreneurs individuels
    if (isEntrepreneurIndividuel()) {
      return 'Non applicable';
    }
    
    // Try different paths based on the API response structure
    if (companyData.formality?.content?.personneMorale?.identite?.description?.montantCapital) {
      const montant = companyData.formality.content.personneMorale.identite.description.montantCapital;
      const devise = companyData.formality.content.personneMorale.identite.description.deviseCapital || 'EUR';
      return `${Number(montant).toLocaleString('fr-FR')} ${devise}`;
    }
    
    if (companyData.formality?.content?.personneMorale?.description?.montantCapital) {
      const montant = companyData.formality.content.personneMorale.description.montantCapital;
      const devise = companyData.formality.content.personneMorale.description.deviseCapital || 'EUR';
      return `${Number(montant).toLocaleString('fr-FR')} ${devise}`;
    }
    
    if (companyData.capital?.montant) {
      return `${Number(companyData.capital.montant).toLocaleString('fr-FR')} ${companyData.capital.devise || 'EUR'}`;
    }
    
    return 'Non renseigné';
  };
  
  // Get company name
  const getCompanyName = () => {
    if (uniteLegale) {
      return uniteLegale.denominationUniteLegale || 
        (uniteLegale.nomUniteLegale && uniteLegale.prenom1UniteLegale ? 
          `${uniteLegale.prenom1UniteLegale} ${uniteLegale.nomUniteLegale}` : '');
    }
    
    return 'Non renseigné';
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Informations de l'entreprise
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Données consolidées INSEE et INPI
        </p>
      </div>
      
      <div className="p-6">
        {!uniteLegale && companyDataLoading ? (
          <div className="space-y-4">
            <Skeleton height={24} width="50%" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Skeleton count={5} />
              </div>
              <div>
                <Skeleton count={5} />
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left column - Base information */}
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Dénomination</h4>
                <p className="mt-1 text-sm text-gray-900 font-medium">
                  {getCompanyName()}
                  {uniteLegale?.sigleUniteLegale && (
                    <span className="ml-2 text-gray-500">({uniteLegale.sigleUniteLegale})</span>
                  )}
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">SIREN</h4>
                <p className="mt-1 text-sm text-gray-900 font-mono">{siren}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Forme juridique</h4>
                <p className="mt-1 text-sm text-gray-900">
                  {uniteLegale?.categorieJuridique ? (
                    <>
                      {uniteLegale.categorieJuridique.label} ({uniteLegale.categorieJuridique.code})
                    </>
                  ) : (
                    `Code: ${uniteLegale?.categorieJuridiqueUniteLegale || 'Non renseigné'}`
                  )}
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Date de création</h4>
                <p className="mt-1 text-sm text-gray-900">
                  {uniteLegale?.dateCreationUniteLegale ? formatDate(uniteLegale.dateCreationUniteLegale) : 'Non renseigné'}
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Statut administratif</h4>
                <p className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    uniteLegale?.etatAdministratifUniteLegale === 'A' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {getAdminStatusLabel(uniteLegale?.etatAdministratifUniteLegale || '')}
                  </span>
                </p>
              </div>
            </div>
            
            {/* Right column - Additional information */}
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Activité principale</h4>
                <p className="mt-1 text-sm text-gray-900">
                  {uniteLegale?.activitePrincipaleUniteLegale || 'Non renseigné'}
                  {uniteLegale?.nafInfo && (
                    <span className="ml-2 text-gray-600">
                      - {uniteLegale.nafInfo.intitule}
                    </span>
                  )}
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Tranche d'effectifs</h4>
                <p className="mt-1 text-sm text-gray-900">
                  {getStaffSizeLabel(uniteLegale?.trancheEffectifsUniteLegale || '')}
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Économie sociale et solidaire</h4>
                <p className="mt-1 text-sm text-gray-900">
                  {getESSLabel(uniteLegale?.economieSocialeSolidaireUniteLegale || '')}
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Capital</h4>
                <p className="mt-1 text-sm text-gray-900">
                  {getCapitalInfo()}
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Nombre d'établissements</h4>
                <p className="mt-1">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {uniteLegale?.etablissementCount || 0}
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* INPI Object Social when available */}
        {companyData?.formality?.content?.personneMorale?.description?.objet && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-500">Objet social (INPI)</h4>
            <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md border border-gray-200">
              {companyData.formality.content.personneMorale.description.objet}
            </p>
          </div>
        )}
        
        {/* Directors section */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-lg font-medium text-gray-800 mb-4">Direction et administration</h4>
          <DirectorsCard 
            companyData={companyData} 
            isLoading={companyDataLoading} 
            error={companyError} 
          />
        </div>
      </div>
    </div>
  );
};

export default CompanyOverviewSection; 